/**
 * useFavourites — pinned asset + investigation tracker.
 *
 * Persists in localStorage under 'rydore.favourites'. Every asset detail
 * page can call `toggle(assetId)` to star/unstar. The sidebar shows a
 * "Pinned" section at the top listing the top 5 pinned items for one-
 * click return. A future iteration syncs this to the backend (per-user
 * preference store) so it roams across devices.
 */
import { useCallback, useEffect, useState } from 'react'

const LS_KEY = 'rydore.favourites'

export interface FavouriteItem {
  id: string                              // asset id or investigation id
  kind: 'asset' | 'investigation'
  label: string                           // display name
  addedAt: string                         // ISO timestamp
  /** Optional route target (default /assets/{id} or /brain/.../?investigation={id}) */
  target?: string
}

function load(): FavouriteItem[] {
  try {
    const raw = localStorage.getItem(LS_KEY)
    if (!raw) return []
    const parsed = JSON.parse(raw)
    if (!Array.isArray(parsed)) return []
    return parsed
  } catch {
    return []
  }
}

function persist(items: FavouriteItem[]): void {
  try {
    localStorage.setItem(LS_KEY, JSON.stringify(items))
  } catch {
    /* silent — localStorage may be full / disabled */
  }
}

export function useFavourites() {
  const [items, setItems] = useState<FavouriteItem[]>(() => load())

  // Keep cross-tab in sync
  useEffect(() => {
    const onStorage = (e: StorageEvent) => {
      if (e.key === LS_KEY) setItems(load())
    }
    window.addEventListener('storage', onStorage)
    return () => window.removeEventListener('storage', onStorage)
  }, [])

  const has = useCallback(
    (id: string) => items.some((i) => i.id === id),
    [items],
  )

  const add = useCallback((item: Omit<FavouriteItem, 'addedAt'>) => {
    setItems((prev) => {
      if (prev.some((p) => p.id === item.id)) return prev
      const next = [{ ...item, addedAt: new Date().toISOString() }, ...prev].slice(0, 20)
      persist(next)
      return next
    })
  }, [])

  const remove = useCallback((id: string) => {
    setItems((prev) => {
      const next = prev.filter((p) => p.id !== id)
      persist(next)
      return next
    })
  }, [])

  const toggle = useCallback((item: Omit<FavouriteItem, 'addedAt'>) => {
    setItems((prev) => {
      const exists = prev.some((p) => p.id === item.id)
      const next = exists
        ? prev.filter((p) => p.id !== item.id)
        : [{ ...item, addedAt: new Date().toISOString() }, ...prev].slice(0, 20)
      persist(next)
      return next
    })
  }, [])

  return { items, has, add, remove, toggle }
}
